import CloseRounded from "@mui/icons-material/CloseRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import EditRounded from "@mui/icons-material/EditRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import Box from "@mui/material/Box";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import { useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import isEmpty from "lodash/isEmpty";
import { useMemo, useRef, useState } from "react";
import { Field } from "react-final-form";
import { v4 as uuidv4 } from "uuid";
import { VALIDATOR_REQUIRE } from "../../../utils/finalForm/validators/VALIDATOR_REQUIRE";
import Txt from "../../typography/Txt";
import RffInputWrapper from "../shared/RffInputWrapper";

const RffFileUploadField = ({
  docGroup,
  docType,
  label,
  required = false,
  suppressGrid,
  size,
  suppressIcon,
  height = "180px",
  uploadText = "Drag & drop files here or click to select files",
  sx,
  suppressButton,
  helperText,
  borderSize = 3,
  background = "transparent",
  allowRename = false, // New prop to enable renaming
}) => {
  const theme = useTheme();
  const dragCounter = useRef(0);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  const validate = useMemo(() => {
    if (!required) return undefined;

    return (value) => {
      // Check if value is an array and not empty
      if (!Array.isArray(value) || value.length === 0) {
        return "At least one file is required";
      }

      // Filter files that match the current docGroup/docType criteria
      const matchingFiles = value.filter((fileObj) => {
        const matchesGroup = docGroup
          ? fileObj.docGroup === docGroup
          : !fileObj.docGroup;
        const matchesType = docType
          ? fileObj.docType === docType
          : !fileObj.docType;

        return matchesGroup && matchesType;
      });

      // Check if there's at least one matching file for this field
      if (matchingFiles.length === 0) {
        return "At least one file is required";
      }

      return undefined; // Valid
    };
  }, [required, docGroup, docType]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      setIsDraggingOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDraggingOver(false);
    }
  };

  const getFileExtension = (filename) => {
    return filename.split(".").pop().toLowerCase();
  };

  const ensureFileExtension = (newName, originalName) => {
    const originalExtension = getFileExtension(originalName);
    const newNameWithoutExt = newName.replace(/\.[^/.]+$/, "");
    return `${newNameWithoutExt}.${originalExtension}`;
  };

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      required={required}
      helperText={helperText}
    >
      <Field
        name="uploadMetadata"
        validate={validate}
        subscription={{ value: true, error: true, touched: true }}
        render={({ input, meta }) => {
          const allFiles = Array.isArray(input.value) ? input.value : [];

          const displayedFiles = allFiles.filter((fileObj) => {
            const matchesGroup = docGroup
              ? fileObj.docGroup === docGroup
              : !fileObj.docGroup;
            const matchesType = docType
              ? fileObj.docType === docType
              : !fileObj.docType;

            return matchesGroup && matchesType;
          });

          const onFilesAdded = (newRawFiles) => {
            const newFiles = newRawFiles.map((rawFile) => {
              const newId = uuidv4();

              return {
                id: newId,
                file: rawFile,
                name: rawFile.name,
                file_display_name: rawFile.name,
                docGroup: docGroup || null,
                docType: docType || null,
              };
            });

            const updatedFiles = [...allFiles, ...newFiles];
            input.onChange(updatedFiles);
          };

          const onFileDelete = (indexInDisplayed) => {
            const fileToRemove = displayedFiles[indexInDisplayed];
            const updatedFiles = allFiles.filter((f) => f !== fileToRemove);
            input.onChange(updatedFiles);
          };

          const onFileRename = (fileId, newName) => {
            const updatedFiles = allFiles.map((f) => {
              if (f.id === fileId) {
                return {
                  ...f,
                  file_display_name: ensureFileExtension(newName, f.name),
                };
              }
              return f;
            });
            input.onChange(updatedFiles);
          };

          const handleDragOver = (e) => {
            e.preventDefault();
            e.stopPropagation();
          };

          const handleDrop = (e) => {
            e.preventDefault();
            e.stopPropagation();
            dragCounter.current = 0;
            setIsDraggingOver(false);
            if (!e.dataTransfer.files) return;
            const droppedFiles = Array.from(e.dataTransfer.files);
            onFilesAdded(droppedFiles);
          };

          const handleFileChange = (e) => {
            if (!e.target.files) return;
            onFilesAdded(Array.from(e.target.files));
          };

          const handleClickDropzone = () => {
            const fileInput = document.getElementById(
              `files-fileinput-${docGroup || "noGroup"}-${docType || "noType"}`
            );
            if (fileInput) fileInput.click();
          };

          const handleStartEdit = (fileObj) => {
            setEditingFileId(fileObj.id);
            setEditingValue(fileObj.file_display_name);
          };

          const handleSaveEdit = () => {
            if (editingFileId && editingValue.trim()) {
              onFileRename(editingFileId, editingValue);
            }
            setEditingFileId(null);
            setEditingValue("");
          };

          const handleCancelEdit = () => {
            setEditingFileId(null);
            setEditingValue("");
          };

          const handleKeyPress = (e) => {
            if (e.key === "Enter") {
              handleSaveEdit();
            } else if (e.key === "Escape") {
              handleCancelEdit();
            }
          };

          return (
            <>
              <Box
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClickDropzone}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexDirection: "column",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 100ms ease-in-out",
                  height,
                  padding: "1rem",
                  gap: 2,
                  borderRadius: "4px",
                  background: isDraggingOver
                    ? `${theme.palette.primary.light}20`
                    : background,
                  // spread user overrides first...
                  ...(sx || {}),
                  // ...then override border and color last when dragging
                  border: isDraggingOver
                    ? `${borderSize}px dashed ${theme.palette.primary.main}`
                    : `${borderSize}px dashed ${grey[300]}`,
                  // color drives children
                  color: isDraggingOver
                    ? theme.palette.primary.main
                    : grey[600],
                  borderBottomLeftRadius: isEmpty(displayedFiles) ? "4px" : "0",
                  borderBottomRightRadius: isEmpty(displayedFiles)
                    ? "4px"
                    : "0",
                }}
              >
                {suppressIcon ? null : (
                  <FileUploadOutlined
                    sx={{ opacity: isDraggingOver ? 0.8 : 0.55 }}
                  />
                )}
                <Txt
                  sx={{
                    fontSize: "13px",
                    opacity: isDraggingOver ? 0.9 : 0.7,
                  }}
                >
                  {uploadText}
                </Txt>
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  // Unique-ish ID so multiple fields won't conflict
                  id={`files-fileinput-${docGroup || "noGroup"}-${docType || "noType"}`}
                />
                {suppressButton ? null : (
                  <Typography
                    sx={{
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: 700,
                      padding: "4px 10px",
                      border: `1px solid ${theme.palette.primary.main}`,
                      borderRadius: "4px",

                      background: isDraggingOver
                        ? `${theme.palette.primary.light}30`
                        : "transparent",
                      color: theme.palette.primary.main,
                    }}
                  >
                    Browse Files
                  </Typography>
                )}
              </Box>
              {meta.touched && meta.error && (
                <FormHelperText error>{meta.error}</FormHelperText>
              )}
              {displayedFiles.length > 0 && (
                <List
                  sx={{
                    listStyle: "none",
                    pt: 0.3,
                    mb: 2,
                    overflow: "hidden",
                    border: `1px solid ${grey[300]}`,
                    borderBottomLeftRadius: "8px",
                    borderTop: 0,
                    borderBottomRightRadius: "8px",
                    p: 0,
                  }}
                >
                  {displayedFiles.map((fileObj, idx) => (
                    <ListItem
                      key={fileObj.id}
                      sx={{
                        background: grey[50],
                        justifyContent: "space-between",
                        alignItems: "center",
                        p: 0,
                        fontSize: "11px !important",
                        display: "flex",
                        borderBottom:
                          displayedFiles?.length !== idx + 1
                            ? `1px solid ${grey[300]}`
                            : "none",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      {editingFileId === fileObj.id ? (
                        <>
                          <TextField
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={handleKeyPress}
                            onBlur={() => handleSaveEdit()}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            size="small"
                            sx={{
                              flex: 1,
                              m: 0.5,
                              "& .MuiInputBase-input": {
                                fontSize: "11px",
                                padding: "4px 8px",
                              },
                            }}
                          />
                          {/* <IconButton
                            color="primary"
                            size="small"
                            sx={{
                              padding: "4px",
                              height: "100%",
                              borderRadius: 0,
                              width: "30px",
                              minWidth: "30px",
                              flexShrink: 0,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveEdit();
                            }}
                          >
                            <CheckRounded sx={{ fontSize: "14px" }} />
                          </IconButton> */}
                          <IconButton
                            color="error"
                            size="small"
                            sx={{
                              padding: "4px",
                              height: "100%",
                              borderRadius: 0,
                              width: "30px",
                              minWidth: "30px",
                              flexShrink: 0,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelEdit();
                            }}
                          >
                            <CloseRounded sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Txt
                            sx={{
                              p: 1,
                              fontSize: "11px !important",
                              flex: 1,
                              minWidth: 0,
                              wordBreak: "break-all",
                              overflow: "hidden",
                              cursor: allowRename ? "text" : "default",
                              "&:hover": allowRename
                                ? {
                                    textDecoration: "underline",
                                    background: grey[100],
                                  }
                                : {},
                            }}
                            onClick={(e) => {
                              if (allowRename) {
                                e.stopPropagation();
                                handleStartEdit(fileObj);
                              }
                            }}
                          >
                            {fileObj.file_display_name}
                          </Txt>
                          {/* {allowRename && (
                            <IconButton
                              color="primary"
                              size="small"
                              sx={{
                                padding: "4px",
                                height: "100%",
                                borderRadius: 0,
                                width: "30px",
                                minWidth: "30px",
                                flexShrink: 0,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEdit(fileObj);
                              }}
                            >
                              <EditRounded sx={{ fontSize: "14px" }} />
                            </IconButton>
                          )} */}
                          <IconButton
                            color="error"
                            size="small"
                            sx={{
                              padding: "4px",
                              height: "100%",
                              borderRadius: 0,
                              width: "30px",
                              minWidth: "30px",
                              flexShrink: 0,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              onFileDelete(idx);
                            }}
                          >
                            <CloseRounded sx={{ fontSize: "14px" }} />
                          </IconButton>
                        </>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </>
          );
        }}
      />
    </RffInputWrapper>
  );
};

export default RffFileUploadField;

// import CloseRounded from "@mui/icons-material/CloseRounded";
// import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
// import Box from "@mui/material/Box";
// import FormHelperText from "@mui/material/FormHelperText";
// import IconButton from "@mui/material/IconButton";
// import List from "@mui/material/List";
// import ListItem from "@mui/material/ListItem";
// import Typography from "@mui/material/Typography";
// import { useTheme } from "@mui/material";
// import { grey } from "@mui/material/colors";
// import isEmpty from "lodash/isEmpty";
// import { useMemo, useRef, useState } from "react";
// import { Field } from "react-final-form";
// import { v4 as uuidv4 } from "uuid";
// import { VALIDATOR_REQUIRE } from "../../../utils/finalForm/validators/VALIDATOR_REQUIRE";
// import Txt from "../../typography/Txt";
// import RffInputWrapper from "../shared/RffInputWrapper";

// const RffFileUploadField = ({
//   docGroup,
//   docType,
//   label,
//   required = false,
//   suppressGrid,
//   size,
//   suppressIcon,
//   height = "180px",
//   uploadText = "Drag & drop files here or click to select files",
//   sx,
//   suppressButton,
//   helperText,
//   borderSize = 3,
//   background = "transparent",
// }) => {
//   const theme = useTheme();
//   const dragCounter = useRef(0);
//   const [isDraggingOver, setIsDraggingOver] = useState(false);

//   const validate = useMemo(() => {
//     if (!required) return undefined;

//     return (value) => {
//       // Check if value is an array and not empty
//       if (!Array.isArray(value) || value.length === 0) {
//         return "At least one file is required";
//       }

//       // Filter files that match the current docGroup/docType criteria
//       const matchingFiles = value.filter((fileObj) => {
//         const matchesGroup = docGroup
//           ? fileObj.docGroup === docGroup
//           : !fileObj.docGroup;
//         const matchesType = docType
//           ? fileObj.docType === docType
//           : !fileObj.docType;

//         return matchesGroup && matchesType;
//       });

//       // Check if there's at least one matching file for this field
//       if (matchingFiles.length === 0) {
//         return "At least one file is required";
//       }

//       return undefined; // Valid
//     };
//   }, [required, docGroup, docType]);

//   const handleDragEnter = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     dragCounter.current += 1;
//     if (dragCounter.current === 1) {
//       setIsDraggingOver(true);
//     }
//   };

//   const handleDragLeave = (e) => {
//     e.preventDefault();
//     e.stopPropagation();
//     dragCounter.current -= 1;
//     if (dragCounter.current === 0) {
//       setIsDraggingOver(false);
//     }
//   };

//   return (
//     <RffInputWrapper
//       label={label}
//       suppressGrid={suppressGrid}
//       size={size}
//       required={required}
//       helperText={helperText}
//     >
//       <Field
//         name="uploadMetadata"
//         validate={validate}
//         subscription={{ value: true, error: true, touched: true }}
//         render={({ input, meta }) => {
//           const allFiles = Array.isArray(input.value) ? input.value : [];

//           const displayedFiles = allFiles.filter((fileObj) => {
//             const matchesGroup = docGroup
//               ? fileObj.docGroup === docGroup
//               : !fileObj.docGroup;
//             const matchesType = docType
//               ? fileObj.docType === docType
//               : !fileObj.docType;

//             return matchesGroup && matchesType;
//           });

//           const onFilesAdded = (newRawFiles) => {
//             const newFiles = newRawFiles.map((rawFile) => {
//               const newId = uuidv4();

//               return {
//                 id: newId,
//                 file: rawFile,
//                 name: rawFile.name,
//                 file_display_name: rawFile.name,
//                 docGroup: docGroup || null,
//                 docType: docType || null,
//               };
//             });

//             const updatedFiles = [...allFiles, ...newFiles];
//             input.onChange(updatedFiles);
//           };

//           const onFileDelete = (indexInDisplayed) => {
//             const fileToRemove = displayedFiles[indexInDisplayed];
//             const updatedFiles = allFiles.filter((f) => f !== fileToRemove);
//             input.onChange(updatedFiles);
//           };

//           const handleDragOver = (e) => {
//             e.preventDefault();
//             e.stopPropagation();
//           };

//           const handleDrop = (e) => {
//             e.preventDefault();
//             e.stopPropagation();
//             dragCounter.current = 0;
//             setIsDraggingOver(false);
//             if (!e.dataTransfer.files) return;
//             const droppedFiles = Array.from(e.dataTransfer.files);
//             onFilesAdded(droppedFiles);
//           };

//           const handleFileChange = (e) => {
//             if (!e.target.files) return;
//             onFilesAdded(Array.from(e.target.files));
//           };

//           const handleClickDropzone = () => {
//             const fileInput = document.getElementById(
//               `files-fileinput-${docGroup || "noGroup"}-${docType || "noType"}`
//             );
//             if (fileInput) fileInput.click();
//           };

//           return (
//             <>
//               <Box
//                 onDragEnter={handleDragEnter}
//                 onDragOver={handleDragOver}
//                 onDragLeave={handleDragLeave}
//                 onDrop={handleDrop}
//                 onClick={handleClickDropzone}
//                 sx={{
//                   display: "flex",
//                   justifyContent: "center",
//                   alignItems: "center",
//                   flexDirection: "column",
//                   textAlign: "center",
//                   cursor: "pointer",
//                   transition: "all 100ms ease-in-out",
//                   height,
//                   padding: "1rem",
//                   gap: 2,
//                   borderRadius: "4px",
//                   background: isDraggingOver
//                     ? `${theme.palette.primary.light}20`
//                     : background,
//                   // spread user overrides first...
//                   ...(sx || {}),
//                   // ...then override border and color last when dragging
//                   border: isDraggingOver
//                     ? `${borderSize}px dashed ${theme.palette.primary.main}`
//                     : `${borderSize}px dashed ${grey[300]}`,
//                   // color drives children
//                   color: isDraggingOver
//                     ? theme.palette.primary.main
//                     : grey[600],
//                   borderBottomLeftRadius: isEmpty(displayedFiles) ? "4px" : "0",
//                   borderBottomRightRadius: isEmpty(displayedFiles)
//                     ? "4px"
//                     : "0",
//                 }}
//               >
//                 {suppressIcon ? null : (
//                   <FileUploadOutlined
//                     sx={{ opacity: isDraggingOver ? 0.8 : 0.55 }}
//                   />
//                 )}
//                 <Txt
//                   sx={{
//                     fontSize: "13px",
//                     opacity: isDraggingOver ? 0.9 : 0.7,
//                   }}
//                 >
//                   {uploadText}
//                 </Txt>
//                 <input
//                   type="file"
//                   multiple
//                   onChange={handleFileChange}
//                   style={{ display: "none" }}
//                   // Unique-ish ID so multiple fields won't conflict
//                   id={`files-fileinput-${docGroup || "noGroup"}-${docType || "noType"}`}
//                 />
//                 {suppressButton ? null : (
//                   <Typography
//                     sx={{
//                       cursor: "pointer",
//                       fontSize: "13px",
//                       fontWeight: 700,
//                       padding: "4px 10px",
//                       border: `1px solid ${theme.palette.primary.main}`,
//                       borderRadius: "4px",

//                       background: isDraggingOver
//                         ? `${theme.palette.primary.light}30`
//                         : "transparent",
//                       color: theme.palette.primary.main,
//                     }}
//                   >
//                     Browse Files
//                   </Typography>
//                 )}
//               </Box>
//               {meta.touched && meta.error && (
//                 <FormHelperText error>{meta.error}</FormHelperText>
//               )}
//               {displayedFiles.length > 0 && (
//                 <List
//                   sx={{
//                     listStyle: "none",
//                     pt: 0.3,
//                     mb: 2,
//                     overflow: "hidden",
//                     border: `1px solid ${grey[300]}`,
//                     borderBottomLeftRadius: "8px",
//                     borderTop: 0,
//                     borderBottomRightRadius: "8px",
//                     p: 0,
//                   }}
//                 >
//                   {displayedFiles.map((fileObj, idx) => (
//                     <ListItem
//                       key={idx}
//                       sx={{
//                         background: grey[50],
//                         justifyContent: "space-between",
//                         alignItems: "center",
//                         p: 0,
//                         fontSize: "11px !important",
//                         display: "flex",
//                         borderBottom:
//                           displayedFiles?.length !== idx + 1
//                             ? `1px solid ${grey[300]}`
//                             : "none",
//                         width: "100%",
//                         boxSizing: "border-box",
//                       }}
//                     >
//                       <Txt
//                         sx={{
//                           p: 1,
//                           fontSize: "11px !important",
//                           flex: 1,
//                           minWidth: 0,
//                           wordBreak: "break-all",
//                           overflow: "hidden",
//                         }}
//                       >
//                         {fileObj.file_display_name}
//                       </Txt>
//                       <IconButton
//                         color="error"
//                         size="small"
//                         sx={{
//                           padding: "4px",
//                           height: "100%",
//                           borderRadius: 0,
//                           width: "30px",
//                           minWidth: "30px",
//                           flexShrink: 0,
//                         }}
//                         onClick={() => onFileDelete(idx)}
//                       >
//                         <CloseRounded sx={{ fontSize: "14px" }} />
//                       </IconButton>
//                     </ListItem>
//                   ))}
//                 </List>
//               )}
//             </>
//           );
//         }}
//       />
//     </RffInputWrapper>
//   );
// };

// export default RffFileUploadField;
